import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from '../ui.jsx';

function GlobalSearch({ db, setPage, isMobile = false }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        if (isMobile) setMobileExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setMobileExpanded(false);
      return;
    }
    if (mobileExpanded && inputRef.current) inputRef.current.focus();
  }, [isMobile, mobileExpanded]);

  const results = React.useMemo(() => {
    if (!q.trim() || !db) return [];
    const lq = q.toLowerCase();
    const out = [];

    // Students
    (db.students || []).filter(s =>
      `${s.fn || ''} ${s.ln || ''}`.toLowerCase().includes(lq) ||
      (s.cls || '').toLowerCase().includes(lq) ||
      (s.ph || '').includes(lq)
    ).slice(0, 5).forEach(s => out.push({
      type: 'student', icon: 'person', label: `${s.fn || ''} ${s.ln || ''}`.trim() || '—', sub: s.cls || '—', page: 'stu'
    }));

    // Teachers
    (db.teachers || []).filter(t =>
      `${t.fn || ''} ${t.ln || ''}`.toLowerCase().includes(lq) ||
      (t.su || '').toLowerCase().includes(lq)
    ).slice(0, 3).forEach(t => out.push({
      type: 'teacher', icon: 'record_voice_over', label: `${t.fn || ''} ${t.ln || ''}`.trim() || '—', sub: t.su || 'Teacher', page: 'tea'
    }));

    // Pages
    const pages = [
      { id: 'dash', label: 'Dashboard', icon: 'dashboard' },
      { id: 'stu', label: 'Students', icon: 'person' },
      { id: 'tea', label: 'Staff', icon: 'badge' },
      { id: 'cls', label: 'Classes', icon: 'class' },
      { id: 'tt', label: 'Timetable', icon: 'schedule' },
      { id: 'satt', label: 'Staff Att.', icon: 'how_to_reg' },
      { id: 'att', label: 'Attendance', icon: 'fact_check' },
      { id: 'exam', label: 'Exams', icon: 'quiz' },
      { id: 'fee', label: 'Finance', icon: 'payments' },
      { id: 'docs', label: 'Documents', icon: 'description' },
      { id: 'rep', label: 'Reports', icon: 'summarize' },
      { id: 'set', label: 'Settings', icon: 'settings' },
      { id: 'cal', label: 'Calendar', icon: 'calendar_month' },
    ];
    pages.filter(p => p.label.toLowerCase().includes(lq)).forEach(p => out.push({
      type: 'page', icon: p.icon, label: p.label, sub: 'Page', page: p.id
    }));

    return out.slice(0, 8);
  }, [q, db]);

  const go = (item) => {
    setPage(item.page);
    setQ('');
    setOpen(false);
    if (isMobile) setMobileExpanded(false);
  };

  const typeColor = { student: '#1960a3', teacher: '#059669', page: '#cb9524' };
  const showMobileInput = !isMobile || mobileExpanded;

  return (
    <div className={`gs-root${isMobile ? ' gs-root-mobile' : ''}${mobileExpanded ? ' gs-mobile-open' : ''}`} ref={ref} style={{ position: 'relative' }}>
      <div className="gs-box" style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: isMobile ? (showMobileInput ? '7px 14px' : '7px') : '7px 14px', borderRadius: 24, width: isMobile ? (showMobileInput ? 240 : 44) : 240,
        background: open ? '#f0f6ff' : '#f4f7fb',
        border: open ? '1.5px solid #1960a3' : '1.5px solid #e2e8f0',
        justifyContent: isMobile && !showMobileInput ? 'center' : 'flex-start',
        transition: 'all 200ms'
      }}>
        <button
          type="button"
          onClick={() => {
            if (isMobile && !mobileExpanded) {
              setMobileExpanded(true);
              setOpen(Boolean(q.trim()));
              return;
            }
            if (isMobile && mobileExpanded && !q.trim()) {
              setMobileExpanded(false);
              setOpen(false);
            }
          }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#94a3b8', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          aria-label={showMobileInput ? 'Collapse search' : 'Open search'}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }}>search</span>
        </button>
        {showMobileInput && (
          <input
            ref={inputRef}
            style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: '#1e293b', width: '100%', fontFamily: 'inherit' }}
            className="gs-input"
            placeholder="Search…"
            value={q}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setQ('');
                setOpen(false);
                if (isMobile) setMobileExpanded(false);
              }
            }}
          />
        )}
        {showMobileInput && q && <button onClick={() => { setQ(''); setOpen(false); if (isMobile) setMobileExpanded(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#94a3b8' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
        </button>}
      </div>

      {showMobileInput && open && results.length > 0 && (
        <div className="gs-results" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: isMobile ? 'min(88vw, 320px)' : 300, background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,32,69,0.14)', border: '1px solid #e0e3e5', zIndex: 999, overflow: 'hidden', animation: 'srch-in 150ms ease' }}>
          <style>{`@keyframes srch-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}} .gs-input::placeholder{color:#94a3b8}`}</style>
          {results.map((r, i) => (
            <button key={i} onClick={() => go(r)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'background 120ms' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f7fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: typeColor[r.type] + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 15, color: typeColor[r.type] }}>{r.icon}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#181c1e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</div>
                <div style={{ fontSize: 10, color: '#74777f' }}>{r.sub}</div>
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color: typeColor[r.type], background: typeColor[r.type] + '15', padding: '2px 7px', borderRadius: 20, textTransform: 'uppercase', flexShrink: 0 }}>{r.type}</span>
            </button>
          ))}
        </div>
      )}
      {showMobileInput && open && q && results.length === 0 && (
        <div className="gs-results" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, width: isMobile ? 'min(82vw, 280px)' : 280, background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,32,69,0.14)', border: '1px solid #e0e3e5', zIndex: 999, padding: '16px', textAlign: 'center' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#c4c6cf' }}>search_off</span>
          <div style={{ fontSize: 12, color: '#74777f', marginTop: 6 }}>No results for "{q}"</div>
        </div>
      )}
    </div>
  );
}


export default GlobalSearch;
